import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

const BASE_URL = '/api/v1';
const ACCESS_KEY = 'ikomet.accessToken';
const REFRESH_KEY = 'ikomet.refreshToken';

export const tokenStore = {
  get access() {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(access: string, refresh: string) {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * The workspace the user is currently in, sent with every request.
 *
 * Held here rather than passed per call because a workspace is a boundary: any
 * screen that forgets to pass it would quietly show the whole organisation,
 * which is exactly what happened when only the project list was scoped.
 *
 * Read from the persisted UI store on load so the very first request after a
 * refresh is already scoped, rather than briefly showing everything.
 */
let activeWorkspaceId: string | null = null;

export const setRequestWorkspace = (id: string | null) => {
  activeWorkspaceId = id;
};

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStore.access;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (activeWorkspaceId) config.headers['X-Workspace-Id'] = activeWorkspaceId;
  return config;
});

/**
 * A single in-flight refresh shared by every 401'd request, so a burst of
 * parallel queries triggers one token rotation rather than a stampede.
 */
let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const refreshToken = tokenStore.refresh;
  if (!refreshToken) throw new Error('No refresh token');

  const { data } = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken });
  tokenStore.set(data.accessToken, data.refreshToken);
  return data.accessToken;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean };

    const isAuthCall = original?.url?.includes('/auth/');
    if (error.response?.status !== 401 || original?._retried || isAuthCall) {
      return Promise.reject(normaliseError(error));
    }

    original._retried = true;
    try {
      refreshPromise = refreshPromise ?? refreshAccessToken();
      const token = await refreshPromise;
      original.headers.Authorization = `Bearer ${token}`;
      return api(original);
    } catch {
      tokenStore.clear();
      // Full reload drops all cached state along with the dead session.
      window.location.assign('/login');
      return Promise.reject(normaliseError(error));
    } finally {
      refreshPromise = null;
    }
  },
);

export interface ApiError extends Error {
  status?: number;
  fieldErrors?: string[];
}

function normaliseError(error: AxiosError): ApiError {
  const payload = error.response?.data as { message?: string | string[] } | undefined;
  const raw = payload?.message;
  const message = Array.isArray(raw) ? raw[0] : raw ?? error.message ?? 'Something went wrong';

  const apiError = new Error(message) as ApiError;
  apiError.status = error.response?.status;
  if (Array.isArray(raw)) apiError.fieldErrors = raw;
  return apiError;
}

/** Unwraps the axios envelope so callers deal in plain data. */
export async function get<T>(url: string, params?: object): Promise<T> {
  const { data } = await api.get<T>(url, { params });
  return data;
}

export async function post<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await api.post<T>(url, body);
  return data;
}

export async function patch<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await api.patch<T>(url, body);
  return data;
}

export async function put<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await api.put<T>(url, body);
  return data;
}

export async function del<T>(url: string): Promise<T> {
  const { data } = await api.delete<T>(url);
  return data;
}
